/**
 * Urlaubskonto-Karte: alle Saldo-Felder eines Jahres, „Verbleibend“ prominent.
 * Wiederverwendet im Konten-Tab (/urlaub) und in der Personalakte.
 */

import { fmtDate, fmtDays } from "@/lib/format"
import { cn } from "@/lib/utils"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { LeaveBalance } from "@/types"

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium tabular-nums">{value}</p>
    </div>
  )
}

export function LeaveBalanceCard({
  balance,
  title,
  actions,
}: {
  balance: LeaveBalance
  title?: string
  actions?: React.ReactNode
}) {
  const remaining = Number(balance.remainingDays)
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          {title ?? `Urlaubskonto ${balance.year}`}
        </CardTitle>
        {actions}
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className={cn("text-3xl font-bold tabular-nums", remaining < 0 && "text-destructive")}>
            {fmtDays(balance.remainingDays)} Tage
          </p>
          <p className="text-xs text-muted-foreground">verbleibend</p>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <Field label="Anspruch" value={`${fmtDays(balance.entitlementDays)} Tage`} />
          <Field label="Zusatztage" value={`${fmtDays(balance.additionalDays)} Tage`} />
          <Field
            label="Übertrag"
            value={
              `${fmtDays(balance.carryoverDays)} Tage` +
              (balance.carryoverExpiresAt
                ? ` (bis ${fmtDate(balance.carryoverExpiresAt)})`
                : "")
            }
          />
          <Field label="Anfangssaldo" value={`${fmtDays(balance.initialBalanceDays)} Tage`} />
          <Field label="Genommen" value={`${fmtDays(balance.takenDays)} Tage`} />
          <Field label="Beantragt" value={`${fmtDays(balance.pendingDays)} Tage`} />
        </div>
      </CardContent>
    </Card>
  )
}
